import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useLancamentos, type LancamentoFinanceiro } from '@hooks/useLancamentos.js'
import { FinanceiroPageSkeleton } from '@components/primitives/Skeleton.js'
import { PageShell } from '@components/layout/PageShell.js'
import { ModePageHeader } from '@components/layout/ModePageHeader.js'
import { getObraConfig } from '@services/crud.js'
import { LancamentoFormModal } from '@components/financeiro/LancamentoFormModal.js'
import { ConfirmDeleteModal } from '@components/financeiro/ConfirmDeleteModal.js'
import { FinanceiroDashboard } from '@components/financeiro/FinanceiroDashboard.js'
import { RelatoriosTab } from '@components/financeiro/RelatoriosTab.js'
import { LancamentosTab } from '@components/financeiro/LancamentosTab.js'
import {
  emptyForm,
  type FormState,
  type LancamentoItem,
  type TabId,
  type TipoFiltro,
  type TipoLanc,
} from '@components/financeiro/financeiroTypes.js'

interface NavPrefill {
  prefillLancamento?: Partial<FormState>
}

export function FinanceiroPage() {
  const location = useLocation()
  const {
    lancamentos,
    orcamentoTotal,
    totalGasto,
    totalReceitas,
    saldo,
    gastoPorCategoria,
    orcamentoPorCategoria,
    isLoading,
    add,
    update,
    remove,
  } = useLancamentos()

  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const saved = localStorage.getItem('fin_activeTab')
    return saved === 'lancamentos' || saved === 'dashboard' || saved === 'relatorios'
      ? saved
      : 'lancamentos'
  })
  const [tipoFiltro, setTipoFiltro] = useState<TipoFiltro>('todos')
  const [formOpen, setFormOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<LancamentoItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<LancamentoItem | null>(null)
  const [formData, setFormData] = useState<FormState>(emptyForm('gasto'))
  const [nomeObra, setNomeObra] = useState('Minha Obra')

  useEffect(() => {
    getObraConfig().then((c) => {
      if (c?.nome) setNomeObra(c.nome)
    })
  }, [])

  useEffect(() => {
    localStorage.setItem('fin_activeTab', activeTab)
  }, [activeTab])

  useEffect(() => {
    const state = location.state as NavPrefill | null
    if (!state?.prefillLancamento) return
    setActiveTab('lancamentos')
    setEditTarget(null)
    setFormData({ ...emptyForm('gasto'), ...state.prefillLancamento })
    setFormOpen(true)
    window.history.replaceState({}, document.title)
  }, [location.state])

  const openNew = useCallback((tipo: TipoLanc) => {
    setEditTarget(null)
    setFormData(emptyForm(tipo))
    setFormOpen(true)
  }, [])

  const openEdit = useCallback((item: LancamentoItem) => {
    setEditTarget(item)
    setFormData({
      tipo: (item.tipo as TipoLanc) ?? 'gasto',
      titulo: item.titulo,
      valor: String(item.valor),
      data: item.data,
      categoria: item.categoria,
      fornecedor: item.fornecedor ?? '',
      formaPagamento: item.formaPagamento,
      status: item.status,
      moduloId: item.moduloId ?? '',
      observacoes: item.observacoes ?? '',
    })
    setFormOpen(true)
  }, [])

  const handleSave = useCallback(
    async (form: FormState) => {
      const payload: Omit<LancamentoFinanceiro, 'id' | 'criadoEm' | 'atualizadoEm'> = {
        tipo: form.tipo,
        titulo: form.titulo.trim(),
        valor: Number(form.valor),
        data: form.data,
        categoria: form.tipo === 'receita' ? 'Outros' : form.categoria || 'Outros',
        formaPagamento: form.formaPagamento,
        status: form.tipo === 'receita' ? 'pago' : form.status,
        ...(form.moduloId && { moduloId: form.moduloId }),
        ...(form.fornecedor && { fornecedor: form.fornecedor.trim() }),
        ...(form.observacoes && { observacoes: form.observacoes.trim() }),
      }
      if (editTarget) await update(editTarget.id, payload)
      else await add(payload)
      setFormOpen(false)
    },
    [editTarget, update, add],
  )

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return
    await remove(deleteTarget.id)
    setDeleteTarget(null)
  }, [deleteTarget, remove])

  if (isLoading) {
    return (
      <PageShell>
        <FinanceiroPageSkeleton label='Carregando finanças…' />
      </PageShell>
    )
  }

  const all = lancamentos as LancamentoItem[]
  const filtered = all.filter((l) => {
    return tipoFiltro === 'todos' || l.tipo === tipoFiltro || (tipoFiltro === 'gasto' && !l.tipo)
  })

  const pctGasto =
    orcamentoTotal > 0 ? Math.min(Math.round((totalGasto / orcamentoTotal) * 100), 100) : 0

  return (
    <PageShell>
      <ModePageHeader
        title='Financeiro'
        subtitle='Orçamento e o que saiu da conta. Parcela paga e material comprado entram aqui.'
      />

      <div
        className='cf3-segment cf3-segment--full'
        role='tablist'
        aria-label='Seções do financeiro'
      >
        <button
          role='tab'
          aria-selected={activeTab === 'lancamentos'}
          className={`cf3-segment__btn${activeTab === 'lancamentos' ? ' cf3-segment__btn--active' : ''}`}
          onClick={() => setActiveTab('lancamentos')}
        >
          Lançamentos
        </button>
        <button
          role='tab'
          aria-selected={activeTab === 'dashboard'}
          className={`cf3-segment__btn${activeTab === 'dashboard' ? ' cf3-segment__btn--active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Resumo
        </button>
        <button
          role='tab'
          aria-selected={activeTab === 'relatorios'}
          className={`cf3-segment__btn${activeTab === 'relatorios' ? ' cf3-segment__btn--active' : ''}`}
          onClick={() => setActiveTab('relatorios')}
        >
          Relatórios
        </button>
      </div>

      <div className='cf3-obra-content'>
        {activeTab === 'dashboard' ? (
          <FinanceiroDashboard
            lancamentos={all}
            orcamentoTotal={orcamentoTotal}
            orcamentoPorCategoria={orcamentoPorCategoria}
          />
        ) : activeTab === 'relatorios' ? (
          <RelatoriosTab lancamentos={all} nomeObra={nomeObra} />
        ) : (
          <LancamentosTab
            saldo={saldo}
            orcamentoTotal={orcamentoTotal}
            totalGasto={totalGasto}
            totalReceitas={totalReceitas}
            pctGasto={pctGasto}
            tipoFiltro={tipoFiltro}
            onTipoFiltroChange={setTipoFiltro}
            all={all}
            filtered={filtered}
            gastoPorCategoria={gastoPorCategoria}
            orcamentoPorCategoria={orcamentoPorCategoria}
            onNew={openNew}
            onEdit={openEdit}
            onDelete={setDeleteTarget}
          />
        )}

        <LancamentoFormModal
          key={editTarget?.id ?? `new-${formData.tipo}`}
          open={formOpen}
          initial={formData}
          title={
            editTarget
              ? editTarget.tipo === 'receita'
                ? 'Editar Receita'
                : 'Editar Gasto'
              : formData.tipo === 'receita'
                ? 'Nova Receita'
                : 'Novo Gasto'
          }
          onSave={handleSave}
          onClose={() => setFormOpen(false)}
        />
        {deleteTarget && (
          <ConfirmDeleteModal
            open
            nome={deleteTarget.titulo}
            onConfirm={handleDelete}
            onClose={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </PageShell>
  )
}
